import React, { useState } from "react";
import api from "../api/axios";
import AdminLayout from "./AdminLayout";

/* ── Toggle Switch ── */
const Toggle = ({ checked, onChange }) => (
  <div
    onClick={onChange}
    style={{
      width:42, height:24, borderRadius:12, cursor:"pointer", position:"relative",
      background: checked ? "#4ade80" : "#2a2d38",
      transition:"background 0.2s",
      flexShrink:0,
    }}
  >
    <div style={{
      position:"absolute", top:3, left: checked ? 21 : 3,
      width:18, height:18, borderRadius:"50%", background:"white",
      transition:"left 0.2s",
    }} />
  </div>
);

const SettingRow = ({ title, desc, checked, onChange, danger }) => (
  <div style={s.settingRow}>
    <div>
      <div style={{ fontSize:14, fontWeight:500, color: danger ? "#f87171" : "#e8e9ed", marginBottom:3 }}>{title}</div>
      <div style={{ fontSize:12, color:"#8b8fa8", lineHeight:1.5 }}>{desc}</div>
    </div>
    <Toggle checked={checked} onChange={onChange} />
  </div>
);

const INITIAL = {
  maintenanceMode:   false,
  notifications:     true,
  autoApproveUsers:  false,
  emailAlerts:       true,
  debugMode:         false,
  carpoolingEnabled: true,
};

const AdminSettings = () => {
  const [settings, setSettings] = useState(INITIAL);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);

  const toggle = (key) => setSettings(p => ({ ...p, [key]: !p[key] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post("/admin/settings", settings);
    } catch { /* fallback */ await new Promise(r=>setTimeout(r,700)); }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const SECTIONS = [
    {
      title:"Plateforme",
      rows:[
        { key:"maintenanceMode",   title:"Mode maintenance",         desc:"Affiche une page de maintenance pour tous les visiteurs.",       danger:true  },
        { key:"notifications",     title:"Notifications système",    desc:"Envoie des alertes aux utilisateurs lors d'événements clés."                  },
        { key:"carpoolingEnabled", title:"Module covoiturage",       desc:"Active ou désactive la section covoiturage sur la plateforme."               },
      ],
    },
    {
      title:"Comptes",
      rows:[
        { key:"autoApproveUsers",  title:"Auto-approbation utilisateurs", desc:"Les nouveaux comptes voyageurs sont approuvés automatiquement."          },
        { key:"emailAlerts",       title:"Alertes email admin",           desc:"Recevez un email à chaque nouvelle inscription ou réservation."          },
      ],
    },
    {
      title:"Développement",
      rows:[
        { key:"debugMode", title:"Mode debug", desc:"Affiche les logs et erreurs détaillés dans la console. Ne pas activer en production.", danger:true },
      ],
    },
  ];

  return (
    <AdminLayout title="Paramètres" subtitle="Configuration de la plateforme">
      <style>{css}</style>

      <div style={s.grid}>
        {/* SETTINGS SECTIONS */}
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          {SECTIONS.map(sec => (
            <div key={sec.title} style={s.card}>
              <div style={s.sectionTitle}>{sec.title}</div>
              {sec.rows.map(row => (
                <SettingRow
                  key={row.key}
                  title={row.title}
                  desc={row.desc}
                  checked={settings[row.key]}
                  onChange={() => toggle(row.key)}
                  danger={row.danger}
                />
              ))}
            </div>
          ))}
        </div>

        {/* RIGHT PANEL */}
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

          {/* SAVE CARD */}
          <div style={s.card}>
            <div style={s.sectionTitle}>Enregistrer</div>
            <p style={{ fontSize:13, color:"#8b8fa8", lineHeight:1.7, marginBottom:20 }}>
              Les modifications s'appliquent immédiatement après sauvegarde. Certains paramètres nécessitent un rechargement.
            </p>
            <button style={{ ...s.saveBtn, opacity: saving ? 0.7 : 1 }} onClick={handleSave} disabled={saving} className="save-btn">
              {saving ? (
                <><span style={s.spin} />Sauvegarde…</>
              ) : saved ? (
                <>✓ Sauvegardé</>
              ) : (
                <>Sauvegarder les paramètres</>
              )}
            </button>
            {saved && (
              <div style={s.savedNote}>✓ Paramètres mis à jour avec succès.</div>
            )}
          </div>

          {/* SUMMARY */}
          <div style={s.card}>
            <div style={s.sectionTitle}>Résumé</div>
            {Object.entries(settings).map(([key, val]) => {
              const labels = {
                maintenanceMode:"Maintenance", notifications:"Notifications", autoApproveUsers:"Auto-approbation",
                emailAlerts:"Alertes email", debugMode:"Debug", carpoolingEnabled:"Covoiturage",
              };
              return (
                <div key={key} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:"0.5px solid #1e2029" }}>
                  <span style={{ fontSize:12, color:"#8b8fa8" }}>{labels[key]}</span>
                  <span style={{ fontSize:11, fontWeight:600, color: val ? "#4ade80" : "#4a4d5a", fontFamily:"'DM Mono',monospace" }}>
                    {val ? "ON" : "OFF"}
                  </span>
                </div>
              );
            })}
          </div>

          {/* DANGER ZONE */}
          <div style={{ ...s.card, borderColor:"rgba(248,113,113,0.2)" }}>
            <div style={{ ...s.sectionTitle, color:"#f87171" }}>Zone danger</div>
            <p style={{ fontSize:12, color:"#8b8fa8", lineHeight:1.6, marginBottom:14 }}>
              Ces actions sont irréversibles. Procédez avec précaution.
            </p>
            <button style={s.dangerBtn}>Réinitialiser tous les paramètres</button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;

const s = {
  grid:         { display:"grid", gridTemplateColumns:"1fr 320px", gap:16, alignItems:"start" },
  card:         { background:"#16181f", border:"0.5px solid #2a2d38", borderRadius:12, padding:"20px 22px" },
  sectionTitle: { fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.8, color:"#8b8fa8", marginBottom:16 },
  settingRow:   { display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:16, padding:"14px 0", borderBottom:"0.5px solid #1e2029" },
  saveBtn:      { width:"100%", padding:"12px 0", background:"#4ade80", color:"#0f1117", border:"none", borderRadius:10, fontSize:13.5, fontWeight:700, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:8 },
  savedNote:    { marginTop:12, fontSize:12, color:"#4ade80", textAlign:"center" },
  spin:         { display:"inline-block", width:13, height:13, border:"2px solid rgba(0,0,0,0.2)", borderTopColor:"#0f1117", borderRadius:"50%", animation:"spin 0.6s linear infinite" },
  dangerBtn:    { width:"100%", padding:"10px 0", background:"rgba(248,113,113,0.1)", color:"#f87171", border:"0.5px solid rgba(248,113,113,0.25)", borderRadius:9, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" },
};

const css = `
  @keyframes spin { to { transform: rotate(360deg); } }
  .save-btn:hover:not(:disabled) { background: #86efac !important; }
  @media(max-width:900px) { .settings-grid { grid-template-columns: 1fr !important; } }
`;